import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, AlertTriangle, FileCode2, RefreshCw, Server, Layout } from "lucide-react";

export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/health/files");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const card = (icon, color, label, val) => (
    <div className="bg-white border border-[#DEE2E6] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>
        {icon}{label}
      </div>
      <div className="mt-1 font-display font-black text-3xl">{val}</div>
    </div>
  );

  const renderBucket = (bucket, title, color, Icon) => {
    if (!bucket) return null;
    return (
      <section className="bg-white border border-[#DEE2E6]" data-testid={`bucket-${title.toLowerCase()}`}>
        <header className="p-5 border-b border-[#DEE2E6] flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold" style={{ color }}>
          <Icon className="h-4 w-4" /> {title} · {bucket.count} fichier{bucket.count > 1 ? "s" : ""} · {bucket.total_lines} lignes
        </header>

        {bucket.criticals.length > 0 && (
          <div className="bg-[#FEF2F2] border-b border-[#DEE2E6] px-5 py-3 flex items-start gap-3" data-testid={`${title.toLowerCase()}-criticals`}>
            <AlertTriangle className="h-4 w-4 text-[#E60000] mt-0.5" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#E60000]">{bucket.criticals.length} fichier(s) critique(s) — &gt; 800 lignes</div>
              <ul className="mt-1 text-xs space-y-1 font-mono">
                {bucket.criticals.map((f) => (
                  <li key={f.path}><span className="font-bold">{f.lines}</span> · {f.path}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8F9FA] text-left text-[10px] uppercase tracking-wider">
                <th className="p-3 w-12">#</th>
                <th className="p-3">Fichier</th>
                <th className="p-3 text-right">Lignes</th>
                <th className="p-3 text-right">Taille</th>
              </tr>
            </thead>
            <tbody data-testid={`${title.toLowerCase()}-rows`}>
              {bucket.top_10.map((f, i) => {
                const isWarn = f.lines > 500;
                const isCrit = f.lines > 800;
                return (
                  <tr key={f.path} className="border-t border-[#F8F9FA] hover:bg-[#F8F9FA]" data-testid={`file-${i}`}>
                    <td className="p-3 text-[#868E96] font-mono">{i + 1}</td>
                    <td className="p-3 font-mono break-all">{f.path}</td>
                    <td className="p-3 text-right font-mono font-bold" style={{ color: isCrit ? "#E60000" : isWarn ? "#D97706" : "#059669" }}>{f.lines}</td>
                    <td className="p-3 text-right text-[#868E96]">{f.size_kb} ko</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Admin · Health"
        title="Dette technique — Lignes par fichier"
        sub="Surveillez la complexité de chaque module et anticipez les refactors."
        right={
          <Button onClick={load} disabled={loading} data-testid="reload-btn" className="h-11 bg-[#002FA7] text-white">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis">
              {card(<Server className="h-3 w-3" />, "#002FA7", "Backend lignes", data.backend.total_lines.toLocaleString("fr-FR"))}
              {card(<Layout className="h-3 w-3" />, "#059669", "Frontend lignes", data.frontend.total_lines.toLocaleString("fr-FR"))}
              {card(<FileCode2 className="h-3 w-3" />, "#D97706", "Total fichiers", data.backend.count + data.frontend.count)}
              {card(<AlertTriangle className="h-3 w-3" />, "#E60000", "Critiques (>800)", data.backend.criticals.length + data.frontend.criticals.length)}
            </div>

            {renderBucket(data.backend, "Backend", "#002FA7", Server)}
            {renderBucket(data.frontend, "Frontend", "#059669", Layout)}

            <div className="text-[10px] text-[#868E96] uppercase tracking-wider flex items-center gap-2">
              <Activity className="h-3 w-3" /> Généré le {new Date(data.generated_at).toLocaleString("fr-FR")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
